import { Helmet } from 'react-helmet-async';
import { ChevronUp } from 'lucide-react';
import { articleImageUrl } from '../utils/articleImageUrl';

/**
 * Solo-article layout for Community → FabVan.
 * Media: frontend/public/article-images/fabvan/ (S3-ready via articleImageUrl).
 */
const HERO = {
  src: 'fabvan/hero.jpg',
  caption:
    'Final render showcasing the facilities inside the van, including 3x 3D printers (Clay and Plastic) to fabricate tiles remotely.',
};

const INTRO =
  'The FabVan is a remote fabrication hub designed to bring 3D printing and shoreline education to schools in Miami-Dade. The aim is to provide an all-day workshop where students get introduced to the benefits of seawall tiles and how 3D printing can make them possible. Students are taught the basic principles of 3D printing, 3D modeling, and helping them bring their 3D models to life.';

type GalleryImage = { type: 'image'; src: string; caption: string };
type GalleryVideo = { type: 'video'; src: string; caption?: string };
type GalleryItem = GalleryImage | GalleryVideo;

const GALLERY: GalleryItem[] = [
  {
    type: 'image',
    src: 'fabvan/a1.jpg',
    caption: 'Initial interior setup, designed by RDF Lab.',
  },
  {
    type: 'video',
    src: 'fabvan/video.mp4',
    caption: 'FabVan in action!',
  },
  {
    type: 'image',
    src: 'fabvan/a2.jpg',
    caption:
      'Earth Day presented the RDF Lab and Institute of Environment the opportunity to host a demo for elementary school children.',
  },
  {
    type: 'image',
    src: 'fabvan/a3.jpg',
    caption: 'Development of the van’s interior, placement of the main tools and items.',
  },
  {
    type: 'image',
    src: 'fabvan/a4.jpg',
    caption:
      'All participant members of the Earth Day celebrations hosted at Frost Art Museum. Team includes: RDF Lab, Institute of Environment & FIU Teach.',
  },
];

export default function FabVan() {
  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{
        background:
          'linear-gradient(165deg, #D8EFEA 0%, #E6F7F4 28%, #C9E6E1 62%, #B5D9D3 100%)',
      }}
    >
      {/* Soft atmospheric wash — keeps the page from reading as a blank sheet */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,255,255,0.55), transparent 55%), radial-gradient(ellipse 60% 40% at 90% 80%, rgba(0,94,110,0.08), transparent 50%), radial-gradient(ellipse 50% 35% at 10% 70%, rgba(0,79,86,0.06), transparent 45%)',
        }}
      />

      <Helmet>
        <title>FabVan | BIOCAP Website</title>
        <meta
          name="description"
          content="BIOCAP FabVan — mobile fabrication and community outreach for living shorelines."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <article className="rounded-2xl bg-[#F3F9F8]/90 backdrop-blur-[2px] border border-[#004F56]/8 shadow-[0_12px_40px_-18px_rgba(0,79,86,0.28)] px-6 sm:px-10 md:px-14 py-12 md:py-16">
          <header className="text-center mb-12 md:mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#004F56] tracking-tight">
              FabVan
            </h1>
            <p className="mt-3 text-lg text-[#004F56]/70">
              Mobile fabrication lab &amp; Community outreach
            </p>
          </header>

          {/* Hero — main van render (from PDF). Border sits on the img so it
              matches the rendered frame (object-contain letterboxing was
              making the ring larger than the visible image). */}
          <figure className="mb-14 md:mb-20 flex flex-col items-center">
            <img
              src={articleImageUrl(HERO.src)}
              alt="FabVan main render"
              className="max-w-full max-h-[75vh] w-auto h-auto rounded-lg ring-1 ring-[#004F56]/15"
            />
            <figcaption className="mt-4 text-center text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
              {HERO.caption}
            </figcaption>
          </figure>

          {/* Main paragraph */}
          <p className="text-lg md:text-xl text-gray-800 leading-8 md:leading-9 mb-16 md:mb-24 max-w-3xl mx-auto text-center md:text-left">
            {INTRO}
          </p>

          {/* Gallery photos / video with captions underneath */}
          <div className="flex flex-col gap-16 md:gap-24">
            {GALLERY.map((item) => (
              <figure key={item.src} className="w-full">
                <div className="overflow-hidden rounded-lg bg-[#E8F3F1] ring-1 ring-[#004F56]/6">
                  {item.type === 'video' ? (
                    <video
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-auto max-h-[65vh] bg-black"
                    >
                      <source
                        src={articleImageUrl(item.src)}
                        type="video/mp4"
                      />
                    </video>
                  ) : (
                    <img
                      src={articleImageUrl(item.src)}
                      alt=""
                      className="w-full h-auto max-h-[65vh] object-cover object-center"
                    />
                  )}
                </div>
                {item.caption ? (
                  <figcaption className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto text-center">
                    {item.caption}
                  </figcaption>
                ) : null}
              </figure>
            ))}
          </div>

          <div className="mt-16 md:mt-20 flex justify-center border-t border-[#004F56]/10 pt-10">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 rounded-full bg-[#004F56] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#003940] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004F56] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F3F9F8]"
            >
              <ChevronUp className="size-4" aria-hidden />
              Back to top
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}
